import { SearchOutlined } from "@ant-design/icons";
import { Card, Divider, Input, Typography } from "antd";
import { useEffect, useState } from "react";
import { httpRequest } from "../../../helpers/api";
import { ChevronDown, ChevronUp } from "react-feather";

const { Text } = Typography;

interface CitySidebarProps {
    selectedCity: string;
    selectedType: string;
    onCityChange: (cityGroupId: string, cityGroupName: string) => void;
    onTypeChange: (type: string) => void;
    userCity?: string;
    maxHeight?: number;
}

const CitySidebar: React.FC<CitySidebarProps> = ({
    selectedCity,
    selectedType,
    onCityChange,
    onTypeChange,
    userCity,
    maxHeight,
}) => {
    const [groupOfCities, setGroupOfCities] = useState<Array<{ cityGroupId: string; cityGroupName: string }>>([]);
    const [citySearch, setCitySearch] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchGroupOfCities = async () => {
            setLoading(true);
            try {
                const response = await httpRequest.get("exchange-boards/areas");
                const areas = (response.data as any)?.payload?.areas || [];
                const sortedAreas = areas.sort((a: any, b: any) => 
                    a.cityGroupName.localeCompare(b.cityGroupName, 'id')
                );

                setGroupOfCities(sortedAreas);
            } catch (err) {
                console.error("Failed to fetch areas", err);
                setGroupOfCities([]);
            } finally {
                setLoading(false);
            }
        };

        fetchGroupOfCities();
    }, []);

    const filteredCities = groupOfCities.filter(group =>
        group.cityGroupName.toLowerCase().includes(citySearch.toLowerCase())
    );

    const handleCityClick = (cityGroupId: string, cityGroupName: string) => {
        if (selectedCity === cityGroupId) {
            // Collapse - clear selection
            onCityChange("", "");
            onTypeChange("");
        } else {
            // Expand - select city
            onCityChange(cityGroupId, cityGroupName);
            onTypeChange("");
        }
    };

    const handleTypeClick = (e: React.MouseEvent, type: string) => {
        e.stopPropagation();
        onTypeChange(type);
    };

    return (
        <Card
            title={
                <span style={{ fontSize: "13px", fontWeight: 600 }}>
                    Search by Area
                </span>
            }
            size="small"
            style={{
                borderRadius: "12px",
                border: "1px solid #f0f0f0",
                width: '100%',
                height: maxHeight && maxHeight > 0 ? `${maxHeight}px` : 'auto',
            }}
            bodyStyle={{ 
                padding: 16,
                height: 'calc(100% - 48px)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}
            loading={loading}
        >
            <Input
                placeholder="Search area"
                prefix={<SearchOutlined style={{ color: "#999" }} />}
                allowClear
                value={citySearch}
                onChange={(e) => setCitySearch(e.target.value)}
                style={{
                    borderRadius: "8px",
                    marginBottom: "12px",
                }}
            />

            <div style={{ 
                flex: 1,
                overflowY: "auto", 
                overflowX: "hidden",
                paddingRight: "8px",
            }}>
                    {filteredCities.length === 0 ? (
                        <Text type="secondary" style={{ fontSize: "13px" }}>
                            No areas available
                        </Text>
                    ) : (
                        filteredCities.map((group, index) => (
                            <>
                                <div
                                    key={group.cityGroupId}
                                    style={{
                                        padding: "10px 0",
                                    }}
                                >
                                    <div
                                        style={{
                                            cursor: "pointer",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                        onClick={() => handleCityClick(group.cityGroupId, group.cityGroupName)}
                                    >
                                        <Text
                                            strong={selectedCity === group.cityGroupId}
                                            style={{
                                                color:
                                                    selectedCity === group.cityGroupId
                                                        ? "#366CF4"
                                                        : "black",
                                                fontSize: "13px",
                                            }}
                                        >
                                            {group.cityGroupName}
                                        </Text>
                                        {selectedCity === group.cityGroupId ? (
                                            <ChevronUp size={16} />
                                        ) : (
                                            <ChevronDown size={16} />
                                        )}
                                    </div>
                                    {selectedCity === group.cityGroupId && (
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: "10px",
                                                paddingLeft: 24,
                                                marginTop: "10px",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    cursor: "pointer",
                                                    fontSize: "13px",
                                                }}
                                                onClick={(e) =>
                                                    handleTypeClick(e, "WTB")
                                                }
                                            >
                                                <span
                                                    style={{
                                                        color: selectedType === "WTB" ? "#366CF4" : "black",
                                                        fontWeight: selectedType === "WTB" ? 700 : 400,
                                                    }}
                                                >
                                                    Hot Buyer
                                                </span>
                                            </div>
                                            <div
                                                style={{
                                                    cursor: "pointer",
                                                    fontSize: "13px",
                                                }}
                                                onClick={(e) =>
                                                    handleTypeClick(e, "WTR")
                                                }
                                            >
                                                <span
                                                    style={{
                                                        color: selectedType === "WTR" ? "#366CF4" : "black",
                                                        fontWeight: selectedType === "WTR" ? 700 : 400,
                                                    }}
                                                >
                                                    Hot Renter
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {index < filteredCities.length - 1 && (
                                    <Divider style={{ margin: 0 }} />
                                )}
                            </>
                        ))
                    )}
                </div>
        </Card>
    );
};

export default CitySidebar;
